import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Eye, ShoppingCart, Heart, Home, GraduationCap, Utensils, DollarSign, Plane, Activity, Scale } from "lucide-react";
import { useState } from "react";

interface GenieExample {
  id: string;
  title: string;
  description: string;
  url: string;
  icon: React.ReactNode;
  gradient: string;
  tags: string[];
}

const GenieExamplesManagement = () => {
  const [examples] = useState<GenieExample[]>([
    {
      id: '1',
      title: 'E-commerce Store',
      description: 'Online shopping with AI-powered customer support',
      url: '/genie-example/ecommerce',
      icon: <ShoppingCart className="w-8 h-8" />,
      gradient: 'from-[#4E9CFF] to-[#3B82F6]',
      tags: ['Retail', 'Support']
    },
    {
      id: '2',
      title: 'Healthcare Clinic',
      description: 'Medical appointments and patient assistance',
      url: '/genie-example/healthcare',
      icon: <Heart className="w-8 h-8" />,
      gradient: 'from-[#D6409F] to-[#BE2A87]',
      tags: ['Medical', 'Booking']
    },
    {
      id: '3',
      title: 'Real Estate',
      description: 'Property tours and buyer inquiries',
      url: '/genie-example/real-estate',
      icon: <Home className="w-8 h-8" />,
      gradient: 'from-blue-800 to-blue-900',
      tags: ['Property', 'Sales']
    },
    {
      id: '4',
      title: 'Online Learning',
      description: 'Study assistant for students and educators',
      url: '/genie-example/education',
      icon: <GraduationCap className="w-8 h-8" />,
      gradient: 'from-[#4E9CFF] to-[#3B82F6]',
      tags: ['Education', 'Support']
    },
    {
      id: '5',
      title: 'Restaurant',
      description: 'Menu recommendations and order assistance',
      url: '/genie-example/restaurant',
      icon: <Utensils className="w-8 h-8" />,
      gradient: 'from-[#D6409F] to-[#BE2A87]',
      tags: ['Food', 'Ordering']
    },
    {
      id: '6',
      title: 'Banking',
      description: 'Financial guidance and account support',
      url: '/genie-example/financial',
      icon: <DollarSign className="w-8 h-8" />,
      gradient: 'from-blue-800 to-blue-900',
      tags: ['Finance', 'Support']
    },
    {
      id: '7',
      title: 'Travel & Tourism',
      description: 'Vacation planning and booking assistance',
      url: '/genie-example/travel',
      icon: <Plane className="w-8 h-8" />,
      gradient: 'from-[#4E9CFF] to-[#3B82F6]',
      tags: ['Travel', 'Booking']
    },
    {
      id: '8',
      title: 'Fitness Studio',
      description: 'Personal training and class scheduling',
      url: '/genie-example/fitness',
      icon: <Activity className="w-8 h-8" />,
      gradient: 'from-[#D6409F] to-[#BE2A87]',
      tags: ['Fitness', 'Wellness']
    },
    {
      id: '9',
      title: 'Legal Services',
      description: 'Legal consultation and case evaluation',
      url: '/genie-example/legal',
      icon: <Scale className="w-8 h-8" />,
      gradient: 'from-blue-800 to-blue-900',
      tags: ['Legal', 'Consultation']
    }
  ]);


  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Genie Examples</h1>
          <p className="text-muted-foreground">
            Manage and preview full-page immersive genie experiences for internal demonstrations and testing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {examples.map((example) => (
            <Card 
              key={example.id} 
              className="group hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
              onClick={() => window.open(example.url, '_blank')}
            >
              {/* Visual Header with Gradient and Icon */}
              <div className={`h-32 bg-gradient-to-br ${example.gradient} flex items-center justify-center text-white relative overflow-hidden`}>
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors"></div>
                <div className="relative z-10 transform group-hover:scale-110 transition-transform">
                  {example.icon}
                </div>
                {/* Decorative circles */}
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full"></div>
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-white/10 rounded-full"></div>
              </div>
              
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600 transition-colors">
                  {example.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {example.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    {example.tags.map((tag) => (
                      <span key={tag} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(example.url, '_blank');
                    }}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Add New Example Card */}
          <Card className="border-dashed border-2 hover:border-primary/50 transition-colors cursor-pointer">
            <CardContent className="flex flex-col items-center justify-center h-full min-h-[300px] text-center">
              <div className="text-muted-foreground space-y-2">
                <div className="text-4xl mb-2">+</div>
                <p className="font-semibold">Add New Example</p>
                <p className="text-sm">Create a new immersive genie experience</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            <strong>Internal Use Only:</strong> These example pages are for demonstration and testing purposes. 
            Each example is a full-page immersive experience with an embedded genie.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GenieExamplesManagement;